import { useEffect } from 'react';

import { CollectionCreateWizard } from 'pages/partners/pages/collections/components/CollectionCreateWizard';
import { GMetrixTableColumnProps } from 'components/GMetrixTable/components';
import { useCollections } from 'pages/partners/pages/collections/context';
import { GMetrixLoader } from 'components/GMetrixLoader/GMetrixLoader';
import { GMetrixTable } from 'components/GMetrixTable/GMetrixTable';
import { helpContent } from 'pages/partners/pages/collections/help';
import { Collection, CollectionApi } from 'apis/CollectionApi';
import { FormattedMessage, useIntl } from 'react-intl';
import { useNotification } from 'app/notifications';
import { PageHeader } from 'components/layout/page';
import { Link } from 'react-router-dom';
import { useSuperState } from 'hooks';

type CollectionRow = Collection & { Id: number };

export const Collections = () => {

    const [state, setState] = useSuperState({
        collections: [] as CollectionRow[],
        loading: true,
        showCreateWizard: false
    });
    const { collections, loading, showCreateWizard } = state;

    const { formatMessage } = useIntl();
    const { permissions } = useCollections();
    const { createToast } = useNotification();

    const loadCollections = async () => {

        try {

            setState({ loading: true });
            const data = await CollectionApi.GetAll();
            setState({
                collections: data.map(c => ({ ...c, Id: c.CollectionId })),
                loading: false
            });

        } catch (e) {

            setState({ loading: false });

        }

    };

    useEffect(() => {

        loadCollections();

    }, []);

    const handleCreateSuccess = () => {

        setState({ showCreateWizard: false });
        createToast(
            formatMessage({ id: 'association_created_successfully.68f79515', defaultMessage: 'Association created successfully' }),
            'success'
        );
        loadCollections();

    };

    const columns: GMetrixTableColumnProps<CollectionRow>[] = [
        {
            field: 'Name',
            title: formatMessage({ id: 'association_name.b1c7b49c', defaultMessage: 'Association Name' })
        },
        {
            field: 'Description',
            title: formatMessage({ id: 'association_description.05abd76e', defaultMessage: 'Association Description' })
        },
        {
            dataType: 'date',
            field: 'DateCreate',
            title: formatMessage({ id: 'created.8d9f8791', defaultMessage: 'Created' })
        },
        {
            dataType: 'date',
            field: 'DateModify',
            title: formatMessage({ id: 'modified.1a433b2e', defaultMessage: 'Modified' })
        },
        {
            cell: ({ dataItem }) => (
                <td style={{ width: '1%' }}>
                    <Link
                        className="btn btn-primary btn-sm"
                        to={`edit?collectionId=${dataItem.CollectionId}`}
                    >
                        {<FormattedMessage defaultMessage="Edit" id="edit.00978daf" />}
                    </Link>
                </td>
            ),
            title: ''
        }
    ];

    return (
        <div className="collections d-flex flex-column gap-3">
            <PageHeader
                cta={permissions.Create ? {
                    disabled: loading,
                    label: formatMessage({ id: 'create_association.c3fc4bc4', defaultMessage: 'Create Association' }),
                    onClick: () => setState({ showCreateWizard: true })
                } : undefined}
                helpContent={helpContent}
                title={formatMessage({ id: 'associations.a5026e86', defaultMessage: 'Associations' })}
            />

            <div className="card card-body">
                {loading ? (
                    <div className={'d-flex align-items-center justify-content-center p-5'}>
                        <GMetrixLoader />
                    </div>
                ) : (
                    <GMetrixTable<CollectionRow>
                        allowExport
                        columnData={columns}
                        rowData={collections}
                        tableName={formatMessage({ id: 'associations.a5026e86', defaultMessage: 'Associations' })}
                    />
                )}
            </div>

            <CollectionCreateWizard
                onClose={() => setState({ showCreateWizard: false })}
                onSuccess={handleCreateSuccess}
                show={showCreateWizard}
            />
        </div>
    );

};
